/**
 * PaymentsSetupBanner — dashboard nudge that prompts firms to connect their
 * Stripe account so their clients can pay invoices online.
 *
 * Visibility rules:
 *  - Only renders when the workspace's Stripe Connect status is
 *    "not_connected" OR "incomplete" (i.e., never set up, or onboarding
 *    abandoned partway).
 *  - Hidden when charges are enabled (the banner has done its job).
 *  - Hidden if the user dismissed it for this workspace
 *    (`localStorage[`payments_banner_dismissed_${workspace_id}`]`).
 *  - Dismissal is per-workspace so switching workspaces shows the banner
 *    again where appropriate.
 *
 * Click "Set up payments" → /settings?tab=payments where the full
 * StripeConnectCard handles the onboarding redirect.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banknote, ArrowRight, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dismissKey = (wsId) => `payments_banner_dismissed_${wsId}`;

export default function PaymentsSetupBanner({ workspaceId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);   // null = loading, {} = loaded
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    // Respect a prior dismissal for this workspace before hitting the API.
    try {
      if (window.localStorage.getItem(dismissKey(workspaceId)) === '1') {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }

    (async () => {
      try {
        const r = await apiFetch(
          `${API_URL}/api/stripe-connect/status?workspace_id=${workspaceId}`,
        );
        if (r.ok) setStatus(await r.json());
        else setStatus({ error: true });
      } catch {
        setStatus({ error: true });
      }
    })();
  }, [workspaceId]);

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(dismissKey(workspaceId), '1');
    } catch { /* ignore */ }
    setDismissed(true);
  };

  // Banner is hidden in three cases:
  //  1. Still loading (avoid flicker)
  //  2. User dismissed it
  //  3. Stripe is fully connected (charges_enabled)
  //  4. Status fetch errored — better to hide than show a wrong CTA
  if (dismissed || !status || status.error) return null;
  if (status.connected && status.charges_enabled) return null;

  const isIncomplete = status.connected && !status.charges_enabled;

  return (
    <div
      className="relative rounded-2xl border border-emerald-200/60 dark:border-emerald-900/40 bg-gradient-to-r from-emerald-50 via-white to-blue-50 dark:from-emerald-950/30 dark:via-zinc-900 dark:to-blue-950/30 p-4 sm:p-5"
      data-testid="payments-setup-banner"
    >
      {/* Dismiss (X) button — top right */}
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        aria-label="Dismiss"
        data-testid="payments-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pr-8">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center">
          <Banknote className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {isIncomplete
              ? 'Finish setting up payments to start accepting invoice payments'
              : 'Set up payments — get paid in 2 business days'}
          </p>
          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">
            {isIncomplete
              ? 'Stripe needs a few more details before your clients can pay.'
              : 'Let your clients pay invoices by card or ACH. 3.5% + $0.30 per card · 0.8% (max $5) per ACH · No monthly fees.'}
          </p>
        </div>

        {/* CTA */}
        <Button
          onClick={() => navigate('/settings?tab=payments')}
          className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="payments-banner-cta"
        >
          {isIncomplete ? 'Finish setup' : 'Set up payments'}
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
